//* Libraries imports
import React from "react";
import { Camera } from "lucide-react";
import { CircularProgress } from "@mui/material";

//* Componentes imports
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { maskNumberInteger } from "@/utils/maskUtils";
import { TutorialSection } from "./TutorialSection";
import { FinancialConfigForm } from "@/features/Element/sections/FinancialConfigForm";

//* Types imports
import { IProductExtended } from "../ProductFormNew";
import { ApiError } from "@/types/api";

export function ProductSection({
  formData,
  setFormData,
  loadingFile,
  saveFile,
  error,
  handleFileChange,
}: {
  formData: Partial<IProductExtended>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<IProductExtended>>>;
  error: ApiError | null;
  handleFileChange: any;
  loadingFile: boolean;
  saveFile: any;
}) {
  return (
    <section className="flex flex-col items-start w-full">
      {/* Product Header */}
      <div>
        <div style={{ position: "relative", display: "flex" }} id="produtos">
          <input
            disabled={loadingFile}
            type="file"
            accept="image/*"
            id={"image"}
            name={"image"}
            onChange={handleFileChange}
            hidden
          />
          <div
            onClick={() => document.getElementById("image")?.click()}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              minWidth: 100,
              minHeight: 100,
              width: 100,
              height: 100,
              borderRadius: 10,
              border: "1px solid lightgrey",
              padding: 5,
              overflow: "hidden",
            }}
          >
            {formData.image && (
              <img
                style={{
                  borderRadius: 10,
                  minWidth: 90,
                  minHeight: 90,
                  objectFit: "cover",
                }}
                src={
                  formData.image
                    ? (formData.image as any) instanceof File
                      ? URL.createObjectURL(formData.image as unknown as File)
                      : formData.image
                    : "/agenda.svg"
                }
              />
            )}
            {!formData.image && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={70}
                style={{ paddingLeft: 5 }}
                height="70"
                viewBox="0 0 20 16"
              >
                <path
                  d="M0.662897 3.35419C0.220942 3.81256 0 4.36119 0 4.99998L0 13.6666C0 14.3057 0.220942 14.8543 0.662897 15.3125C1.10489 15.771 1.63392 16 2.24999 16H2.89279V2.66658H2.24999C1.63378 2.66658 1.10478 2.89582 0.662897 3.35419Z"
                  fill="#1777CF"
                />
                <path
                  d="M12.8571 0.999959C12.8571 0.722158 12.7634 0.486097 12.5758 0.291629C12.3885 0.097161 12.1607 0 11.8929 0L6.10719 0C5.83938 0 5.61172 0.097161 5.42417 0.291629C5.23665 0.485987 5.14292 0.722122 5.14292 0.999959V2.66658H3.8572V16H14.1429V2.66658H12.8571V0.999959ZM11.5715 2.66658H6.42861V1.33329H11.5715V2.66658Z"
                  fill="#1777CF"
                />
                <path
                  d="M17.3371 3.35419C16.8951 2.89586 16.366 2.66658 15.75 2.66658H15.1071V16H15.75C16.3661 16 16.8951 15.771 17.3371 15.3125C17.779 14.8543 18 14.3056 18 13.6666V4.99998C18 4.36119 17.7789 3.81256 17.3371 3.35419Z"
                  fill="#1777CF"
                />
              </svg>
            )}
          </div>
          <div
            onClick={() => document.getElementById("image")?.click()}
            style={{
              cursor: "pointer",
              boxShadow: "rgba(0, 0, 0, 0.2) -1px 2px 1px 0px",
              padding: 2,
              position: "absolute",
              top: 80,
              left: 70,
              background: "white",
              borderRadius: 10,
              overflow: "hidden",
            }}
          >
            <Camera width={18} height={18} />
          </div>
          <div style={{ padding: 10, paddingTop: 20 }}>
            <p style={{ fontSize: 14, color: "grey" }}>
              Produto: {formData?.number_product}
            </p>
            <p style={{ fontSize: 16 }}>
              <b>{formData?.name}</b>
            </p>
            {loadingFile && <CircularProgress size={18} />}
          </div>
        </div>
      </div>

      <Separator className="w-full my-6" />

      <div className="w-full grid grid-cols-1 gap-6">
        {/* Title Field */}

        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-gray-700">
            Nº do produto <b style={{ color: "red" }}>*</b>
          </label>
          <Input
            className={`h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
              error?.validationErrors?.number_product ? "border-red-500" : ""
            }`}
            value={formData.number_product || ""}
            required
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                number_product: maskNumberInteger(e.target.value),
              }))
            }
            placeholder="Digite o número do produto"
          />
          {error?.validationErrors?.number_product && (
            <p className="text-sm text-red-500 mt-1">
              {error.validationErrors.number_product.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-gray-700">
            Título <b style={{ color: "red" }}>*</b>
          </label>
          <Input
            className={`h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
              error?.validationErrors?.name ? "border-red-500" : ""
            }`}
            value={formData.name || ""}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, name: e.target.value }))
            }
            placeholder="Digite o nome do produto"
          />
          {error?.validationErrors?.name && (
            <p className="text-sm text-red-500 mt-1">
              {error.validationErrors.name.message}
            </p>
          )}
        </div>

        {/* Description Field */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-gray-700">
            Descrição
          </label>
          <Textarea
            className={`min-h-[120px] rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
              error?.validationErrors?.description ? "border-red-500" : ""
            }`}
            placeholder="Digite a descrição do produto..."
            value={formData.description || ""}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, description: e.target.value }))
            }
          />
          {error?.validationErrors?.description && (
            <p className="text-sm text-red-500 mt-1">
              {error.validationErrors.description.message}
            </p>
          )}
        </div>

        {/* Tutorial Section */}
        <TutorialSection
          tutorial={formData.tutorial}
          onChange={(newTutorial) =>
            setFormData((prev) => ({ ...prev, tutorial: newTutorial }))
          }
          onUpload={saveFile}
          loadingFile={loadingFile}
        />

        <FinancialConfigForm
          config={formData.financialConfig}
          error={error}
          onChange={(newConfig) =>
            setFormData((prev) => ({ ...prev, financialConfig: newConfig }))
          }
        />
      </div>
    </section>
  );
}
